import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useLanguage } from '../LanguageContext';
import {
  LEGAL_FORMS, ID_DOC_TYPES, PURPOSES, PAYMENT_METHODS,
  SOURCES_OF_FUNDS, BANKS, COUNTRIES
} from '../constants/kyc';
import { isValidNationalId, isValidEuVat, countryIso } from '../utils/kycValidators';
import { getRegions } from '../constants/regions';
import './Profile.css';

// Only ex-Yugoslav countries require personal ID (EMŠO/JMBG).
const EX_YUGOSLAV_COUNTRIES = ['SI', 'HR', 'BA', 'RS', 'ME', 'MK'];

function isExYugoslav(citizenship) {
  return EX_YUGOSLAV_COUNTRIES.includes(countryIso(citizenship));
}

// Fields the customer can edit through the profile PATCH endpoint.
const EDITABLE_FIELDS = [
  'is_legal_entity', 'first_name', 'last_name',
  'company_name', 'legal_form', 'company_tax_number', 'company_registration_number',
  'company_register_country', 'phone',
  'address_street', 'address_postal_code', 'address_city', 'address_region', 'address_country',
  'has_temp_address', 'temp_address_street', 'temp_address_postal_code',
  'temp_address_city', 'temp_address_country',
  'birth_date', 'birthplace', 'birth_country',
  'tax_number', 'emso',
  'citizenship', 'dual_citizenship', 'dual_citizenship_country', 'sanctions_connection',
  'id_doc_type', 'id_doc_number', 'id_doc_issuer', 'id_doc_issue_date',
  'id_doc_expiry_date', 'id_doc_permanent',
  'pep_status', 'pep_details',
  'buy_volume_per_year', 'purpose', 'purpose_other',
  'source_of_funds', 'source_of_funds_other', 'payment_method',
  'recommendation_from', 'heard_about_us', 'heard_about_us_other', 'customer_comment',
  'sepa_iban', 'sepa_bic', 'sepa_bank_name', 'custom_bank_name'
];

function Profile() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const loadProfile = async () => {
    const res = await authFetch('/api/auth/profile');
    const data = await res.json();
    if (res.ok) {
      setProfile(data);
      const c = data.customer;
      const init = {};
      for (const f of EDITABLE_FIELDS) {
        init[f] = c[f] === null || c[f] === undefined ? '' : c[f];
      }
      init.source_of_funds = c.source_of_funds || [];
      setFormData(init);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadProfile();
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [authFetch]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSourceOfFunds = (source) => {
    setFormData(prev => {
      const current = prev.source_of_funds || [];
      return {
        ...prev,
        source_of_funds: current.includes(source)
          ? current.filter(s => s !== source)
          : [...current, source]
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await authFetch('/api/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: t('profile.messages.saveSuccess') });
        await loadProfile();
      } else {
        setMessage({ type: 'error', text: data.error || t('profile.messages.saveError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('profile.messages.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: t('profile.messages.passwordMismatch') });
      return;
    }
    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: t('profile.messages.passwordTooShort') });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await authFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: t('profile.messages.passwordChangeSuccess') });
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        setMessage({ type: 'error', text: data.error || t('profile.messages.passwordChangeError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('profile.messages.passwordChangeError') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const isLegal = formData.is_legal_entity === true;

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>{t('profile.header.title')}</h1>
        <p>{t('profile.header.subtitle')}</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      <div className="profile-tabs">
        <button className={`tab ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}>{t('profile.tabs.personal')}</button>
        <button className={`tab ${activeTab === 'document' ? 'active' : ''}`}
          onClick={() => setActiveTab('document')}>{t('profile.tabs.document')}</button>
        <button className={`tab ${activeTab === 'verification' ? 'active' : ''}`}
          onClick={() => setActiveTab('verification')}>{t('profile.tabs.verification')}</button>
        <button className={`tab ${activeTab === 'banking' ? 'active' : ''}`}
          onClick={() => setActiveTab('banking')}>{t('profile.tabs.banking')}</button>
        <button className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}>{t('profile.tabs.security')}</button>
      </div>

      <div className="profile-content">
        {/* Personal / company + address + birth */}
        {activeTab === 'personal' && (
          <div className="profile-section">
            <h2>{t('profile.personal.heading')}</h2>

            <div className="form-grid">
              <div className="form-field">
                <label>{t('profile.personal.emailLabel')}</label>
                <input type="email" value={profile?.customer?.email || ''} disabled className="disabled" />
                <span className="field-hint">{t('profile.personal.emailHint')}</span>
              </div>

              {!isLegal ? (
                <>
                  <div className="form-field">
                    <label>{t('profile.personal.firstNameLabel')}</label>
                    <input type="text" value={formData.first_name || ''}
                      onChange={(e) => updateField('first_name', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>{t('profile.personal.lastNameLabel')}</label>
                    <input type="text" value={formData.last_name || ''}
                      onChange={(e) => updateField('last_name', e.target.value)} />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-field">
                    <label>{t('profile.personal.companyNameLabel')}</label>
                    <input type="text" value={formData.company_name || ''}
                      onChange={(e) => updateField('company_name', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>{t('profile.personal.legalFormLabel')}</label>
                    <select value={formData.legal_form || ''}
                      onChange={(e) => updateField('legal_form', e.target.value)}>
                      <option value="">{t('profile.common.selectPlaceholder')}</option>
                      {LEGAL_FORMS.map(f => <option key={f.value} value={f.value}>{t(f.labelKey)}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('profile.personal.companyTaxNumberLabel')}</label>
                    <input type="text" value={formData.company_tax_number || ''}
                      onChange={(e) => updateField('company_tax_number', e.target.value)} />
                    {formData.company_tax_number &&
                      !isValidEuVat(formData.company_tax_number, countryIso(formData.company_register_country)) && (
                      <span className="field-error">{t('profile.personal.companyTaxNumberError')}</span>
                    )}
                  </div>
                  <div className="form-field">
                    <label>{t('profile.personal.registrationNumberLabel')}</label>
                    <input type="text" value={formData.company_registration_number || ''}
                      onChange={(e) => updateField('company_registration_number', e.target.value)} />
                  </div>
                </>
              )}

              <div className="form-field">
                <label>{t('profile.personal.phoneLabel')}</label>
                <input type="tel" value={formData.phone || ''}
                  onChange={(e) => updateField('phone', e.target.value)} placeholder="+386 XX XXX XXX" />
              </div>

              <div className="form-field">
                <label>{t('profile.personal.taxNumberLabel')}</label>
                <input type="text" value={formData.tax_number || ''}
                  onChange={(e) => updateField('tax_number', e.target.value)} placeholder="SI12345678" />
              </div>

              {isExYugoslav(formData.citizenship) && (
                <div className="form-field">
                  <label>{t('profile.personal.personalIdLabel')}</label>
                  <input type="text" value={formData.emso || ''}
                    onChange={(e) => updateField('emso', e.target.value)}
                    placeholder={t('profile.personal.personalIdPlaceholder')} />
                  {formData.emso && !isValidNationalId(formData.emso, countryIso(formData.citizenship)) && (
                    <span className="field-error">{t('profile.personal.nationalIdError')}</span>
                  )}
                </div>
              )}

              <div className="form-field">
                <label>{t('profile.personal.birthDateLabel')}</label>
                <input type="date" value={formData.birth_date || ''}
                  onChange={(e) => updateField('birth_date', e.target.value)} max={today} />
              </div>
              <div className="form-field">
                <label>{t('profile.personal.birthplaceLabel')}</label>
                <input type="text" value={formData.birthplace || ''}
                  onChange={(e) => updateField('birthplace', e.target.value)} />
              </div>
            </div>

            <h3 className="profile-subtitle">{t('profile.address.heading')}</h3>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>{t('profile.address.streetLabel')}</label>
                <input type="text" value={formData.address_street || ''}
                  onChange={(e) => updateField('address_street', e.target.value)} />
              </div>
              <div className="form-field">
                <label>{t('profile.address.postalCodeLabel')}</label>
                <input type="text" value={formData.address_postal_code || ''}
                  onChange={(e) => updateField('address_postal_code', e.target.value)} />
              </div>
              <div className="form-field">
                <label>{t('profile.address.cityLabel')}</label>
                <input type="text" value={formData.address_city || ''}
                  onChange={(e) => updateField('address_city', e.target.value)} />
              </div>
              <div className="form-field">
                <label>{t('profile.address.countryLabel')}</label>
                <select value={formData.address_country || 'Slovenia'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev, address_country: e.target.value, address_region: ''
                  }))}>
                  {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>{t('profile.address.regionLabel')}</label>
                {getRegions(countryIso(formData.address_country || 'Slovenia')) ? (
                  <select value={formData.address_region || ''}
                    onChange={(e) => updateField('address_region', e.target.value)}>
                    <option value="">{t('profile.address.selectRegionPlaceholder')}</option>
                    {getRegions(countryIso(formData.address_country || 'Slovenia')).map(r =>
                      <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <input type="text" value={formData.address_region || ''}
                    onChange={(e) => updateField('address_region', e.target.value)}
                    placeholder={t('profile.address.regionPlaceholder')} />
                )}
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('profile.actions.saving') : t('profile.actions.save')}
              </button>
            </div>
          </div>
        )}

        {/* Document */}
        {activeTab === 'document' && (
          <div className="profile-section">
            <h2>{t('profile.document.heading')}</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>{t('profile.document.typeLabel')}</label>
                <select value={formData.id_doc_type || ''}
                  onChange={(e) => updateField('id_doc_type', e.target.value)}>
                  <option value="">{t('profile.document.selectTypePlaceholder')}</option>
                  {ID_DOC_TYPES.map(docType => <option key={docType.value} value={docType.value}>{t(docType.labelKey)}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>{t('profile.document.numberLabel')}</label>
                <input type="text" value={formData.id_doc_number || ''}
                  onChange={(e) => updateField('id_doc_number', e.target.value)} />
              </div>
              <div className="form-field">
                <label>{t('profile.document.issuerLabel')}</label>
                <input type="text" value={formData.id_doc_issuer || ''}
                  onChange={(e) => updateField('id_doc_issuer', e.target.value)} />
              </div>
              <div className="form-field">
                <label>{t('profile.document.issueDateLabel')}</label>
                <input type="date" value={formData.id_doc_issue_date || ''}
                  onChange={(e) => updateField('id_doc_issue_date', e.target.value)} max={today} />
              </div>
              <div className="form-field">
                <label>{t('profile.document.expiryDateLabel')}</label>
                <input type="date" value={formData.id_doc_expiry_date || ''}
                  onChange={(e) => updateField('id_doc_expiry_date', e.target.value)}
                  disabled={formData.id_doc_permanent === true} min={today} />
              </div>
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.id_doc_permanent === true}
                    onChange={(e) => updateField('id_doc_permanent', e.target.checked)} />
                  {t('profile.document.permanentLabel')}
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('profile.actions.saving') : t('profile.actions.save')}
              </button>
            </div>
          </div>
        )}

        {/* Verification */}
        {activeTab === 'verification' && (
          <div className="profile-section">
            <h2>{t('profile.verification.heading')}</h2>

            <div className="form-subsection">
              <h3>{t('profile.verification.citizenshipHeading')}</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label>{t('profile.verification.citizenshipLabel')}</label>
                  <select value={formData.citizenship || 'Slovenia'}
                    onChange={(e) => updateField('citizenship', e.target.value)}>
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.dual_citizenship === true}
                    onChange={(e) => updateField('dual_citizenship', e.target.checked)} />
                  {t('profile.verification.dualCitizenshipLabel')}
                </label>
              </div>
              {formData.dual_citizenship === true && (
                <div className="form-field">
                  <label>{t('profile.verification.dualCitizenshipCountryLabel')}</label>
                  <select value={formData.dual_citizenship_country || ''}
                    onChange={(e) => updateField('dual_citizenship_country', e.target.value)}>
                    <option value="">{t('profile.common.selectPlaceholder')}</option>
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                  </select>
                </div>
              )}
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.sanctions_connection === true}
                    onChange={(e) => updateField('sanctions_connection', e.target.checked)} />
                  {t('profile.verification.sanctionsLabel')}
                </label>
              </div>
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.pep_status === true}
                    onChange={(e) => updateField('pep_status', e.target.checked)} />
                  {t('profile.verification.pepLabel')}
                </label>
              </div>
              {formData.pep_status === true && (
                <div className="form-field">
                  <label>{t('profile.verification.pepDetailsLabel')}</label>
                  <input type="text" value={formData.pep_details || ''}
                    onChange={(e) => updateField('pep_details', e.target.value)} />
                </div>
              )}
            </div>

            <div className="form-subsection">
              <h3>{t('profile.verification.transactionHeading')}</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label>{t('profile.verification.buyVolumeLabel')}</label>
                  <input type="number" min="0" value={formData.buy_volume_per_year || ''}
                    onChange={(e) => updateField('buy_volume_per_year', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>{t('profile.verification.purposeLabel')}</label>
                  <select value={formData.purpose || ''}
                    onChange={(e) => updateField('purpose', e.target.value)}>
                    <option value="">{t('profile.common.selectPlaceholder')}</option>
                    {PURPOSES.map(p => <option key={p.value} value={p.value}>{t(p.labelKey)}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>{t('profile.verification.paymentMethodLabel')}</label>
                  <select value={formData.payment_method || ''}
                    onChange={(e) => updateField('payment_method', e.target.value)}>
                    <option value="">{t('profile.common.selectPlaceholder')}</option>
                    {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{t(m.labelKey)}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-subsection">
              <h3>{t('profile.verification.sourceOfFundsHeading')}</h3>
              <div className="checkbox-grid">
                {SOURCES_OF_FUNDS.map(source => (
                  <label key={source.value} className="checkbox-label">
                    <input type="checkbox"
                      checked={formData.source_of_funds?.includes(source.value)}
                      onChange={() => toggleSourceOfFunds(source.value)} />
                    {t(source.labelKey)}
                  </label>
                ))}
              </div>
              {formData.source_of_funds?.includes('ostalo') && (
                <div className="form-field" style={{ marginTop: '1rem' }}>
                  <label>{t('profile.verification.sourceOfFundsOtherLabel')}</label>
                  <input type="text" value={formData.source_of_funds_other || ''}
                    onChange={(e) => updateField('source_of_funds_other', e.target.value)} />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('profile.actions.saving') : t('profile.actions.save')}
              </button>
            </div>
          </div>
        )}

        {/* Banking */}
        {activeTab === 'banking' && (
          <div className="profile-section">
            <h2>{t('profile.banking.heading')}</h2>
            <p className="section-description">{t('profile.banking.description')}</p>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>{t('profile.banking.ibanLabel')}</label>
                <input type="text" value={formData.sepa_iban || ''}
                  onChange={(e) => updateField('sepa_iban', e.target.value.toUpperCase())}
                  placeholder="SI56 1234 5678 9012 345" />
              </div>
              <div className="form-field">
                <label>{t('profile.banking.bicLabel')}</label>
                <input type="text" value={formData.sepa_bic || ''}
                  onChange={(e) => updateField('sepa_bic', e.target.value.toUpperCase())}
                  placeholder="LJBASI2X" maxLength="11" />
              </div>
              <div className="form-field">
                <label>{t('profile.banking.bankNameLabel')}</label>
                <select value={formData.sepa_bank_name || ''}
                  onChange={(e) => updateField('sepa_bank_name', e.target.value)}>
                  {BANKS.map(b => <option key={b.value} value={b.value}>{b.labelKey ? t(b.labelKey) : b.label}</option>)}
                </select>
              </div>
              {formData.sepa_bank_name === 'other' && (
                <div className="form-field">
                  <label>{t('profile.banking.customBankNameLabel')}</label>
                  <input type="text" value={formData.custom_bank_name || ''}
                    onChange={(e) => updateField('custom_bank_name', e.target.value)} />
                </div>
              )}
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('profile.actions.saving') : t('profile.actions.save')}
              </button>
            </div>
          </div>
        )}

        {/* Security */}
        {activeTab === 'security' && (
          <div className="profile-section">
            <h2>{t('profile.security.heading')}</h2>
            <form onSubmit={handleChangePassword}>
              <div className="form-grid">
                <div className="form-field full-width">
                  <label>{t('profile.security.currentPasswordLabel')}</label>
                  <input type="password" value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="••••••••" required />
                </div>
                <div className="form-field">
                  <label>{t('profile.security.newPasswordLabel')}</label>
                  <input type="password" value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder={t('profile.security.newPasswordPlaceholder')} required minLength="8" />
                </div>
                <div className="form-field">
                  <label>{t('profile.security.confirmPasswordLabel')}</label>
                  <input type="password" value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder={t('profile.security.confirmPasswordPlaceholder')} required />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('profile.security.changing') : t('profile.security.changePassword')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
